import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  User,
  ShoppingCart,
  Search,
  CheckCircle,
  Check,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { translations2 } from "../i18n/translation";
import { setAuthModalOpen } from "store/features/authModalSlice";
import { motion, AnimatePresence } from "framer-motion";

import { Facebook, Instagram, Twitter } from "lucide-react";
import { FaTiktok } from "react-icons/fa";
import { facebooklink, instagramlink } from "./SocialMediaLink";
import { menuCategory } from "components/Menu/Datamenu";
import { addCategoryToCart } from "store/features/cartSlice";
import { logout } from "store/features/userSlice";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);

  const cartItems = useSelector((state) => state.cart.cart);
  const tip = useSelector((state) => state.cart.tip);
  const lang = useSelector((state) => state.language.lang);
  const isAuthOpen = useSelector((state) => state.authModal.authModalOpen);

  const user = useSelector((state) => state.user.user);
  // console.log({ user });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const t2 = translations2[lang] || translations2.en;

  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // console.log({ t2 });

  const cartCount =
    cartItems?.reduce((total, item) => total + (item.count || 1), 0) || 0;

  const cartSubTotal =
    cartItems?.reduce(
      (total, item) => total + item.price * (item.count || 1),
      0,
    ) || 0;

  const cartTotal = cartSubTotal || 0;

  const goHome = () => {
    setOpen(false);
    setActiveSubmenu(null);

    if (location.pathname !== "/") {
      navigate("/");
      return;
    }

    const el = document.querySelector("#hero");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const goTo = (path) => {
    setOpen(false);
    setActiveSubmenu(null);
    navigate(path);
  };

  const MobileMenuDivider = () => (
    <div className="relative my-3 flex items-center justify-center">
      {/* left dotted line */}
      <div className="flex-1 border-t border-dashed border-neonYellow/50"></div>

      {/* center dots */}
      <div className="mx-4 flex items-center gap-1">
        <span className="w-1 h-1 bg-neonYellow rounded-full"></span>
        <span className="w-1 h-1 bg-neonYellow rounded-full"></span>
        <span className="w-1 h-1 bg-neonYellow rounded-full"></span>
        <span className="w-1 h-1 bg-neonYellow rounded-full"></span>
        <span className="w-1 h-1 bg-neonYellow rounded-full"></span>
      </div>

      {/* right dotted line */}
      <div className="flex-1 border-t border-dashed border-gray-600/50"></div>
    </div>
  );

  const WriteSection = () => {
    return (
      <div className="px-6 py-10 text-center border-0 border-border space-y-6 mt-6">
        {/* WRITE */}
        <div className="space-y-1">
          <p className="text-xs tracking-widest text-gray-400 uppercase">
            Write
          </p>
          <p className="text-sm text-neonYellow">hello.saigonstar@gmail.com</p>
        </div>

        {/* VISIT */}
        <div className="space-y-1">
          <p className="text-xs tracking-widest text-gray-400 uppercase">
            Visit
          </p>
          <p className="text-sm text-neonYellow">
            184 Clarence Street, Upper Riccarton, Christchurch, New Zealand.
          </p>
        </div>

        {/* CALL */}
        <div className="space-y-1">
          <p className="text-xs tracking-widest text-gray-400 uppercase">
            Call
          </p>
          <p className="text-sm text-neonYellow">(+64) 3 348 8879</p>
        </div>

        <MobileMenuDivider />

        {/* SOCIAL ICONS */}
        <div className="flex justify-center gap-6 pt-0 text-gray-400">
          <a href={facebooklink} className="hover:text-neonYellow transition">
            <Facebook size={18} />
          </a>
          <a href={instagramlink} className="hover:text-neonYellow transition">
            <Instagram size={18} />
          </a>
        </div>
      </div>
    );
  };

  const scrollToSection = (id) => {
    setOpen(false);
    setActiveSubmenu(null);

    if (location.pathname !== "/") {
      navigate("/", { replace: false });

      // wait for page render then scroll
      setTimeout(() => {
        const el = document.querySelector(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      const el = document.querySelector(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleUserClick = () => {
    if (!user) {
      dispatch(setAuthModalOpen(true));
      return;
    }

    setUserMenuOpen((prev) => !prev);
  };

  const UserDropdown = ({ open, onClose, onLogout }) => {
    if (!open) return null;

    return (
      <div className="absolute right-0 mt-3 w-44 bg-navy border border-gray-700 shadow-xl rounded-lg overflow-hidden z-50">
        <button
          onClick={() => {
            navigate("/dashboard");
            onClose();
          }}
          className="w-full text-left px-4 py-3 text-sm text-neonYellow hover:bg-white/5 transition"
        >
          Profile
        </button>

        <button
          onClick={() => {
            navigate("/dashboard/listoforders");
            onClose();
          }}
          className="w-full text-left px-4 py-3 text-sm text-neonYellow hover:bg-white/5 transition"
        >
          My Orders
        </button>

        <button
          onClick={onLogout}
          className="w-full text-left px-4 py-3 text-sm text-neonYellow border-t border-gray-600 border-dotted font-semibold hover:bg-white/5 transition"
        >
          Logout
        </button>
      </div>
    );
  };

  const UserDropdownMobileView = ({ open, onClose, onLogout }) => {
    if (!open) return null;

    return (
      <div className="absolute left-0 mt-3 w-44 bg-navy border border-gray-700 shadow-xl rounded-lg overflow-hidden z-50">
        <button
          onClick={() => {
            navigate("/dashboard");
            onClose();
          }}
          className="w-full text-left px-4 py-3 text-sm text-neonYellow hover:bg-white/5 transition"
        >
          Profile
        </button>

        <button
          onClick={() => {
            navigate("/dashboard/listoforders");
            onClose();
          }}
          className="w-full text-left px-4 py-3 text-sm text-neonYellow hover:bg-white/5 transition"
        >
          My Orders
        </button>

        <button
          onClick={onLogout}
          className="w-full text-left px-4 py-3 text-sm text-neonYellow border-t border-gray-600 border-dotted font-semibold hover:bg-white/5 transition"
        >
          Logout
        </button>
      </div>
    );
  };

  const handleLogout = () => {
    dispatch(logout());
    setUserMenuOpen(false);
    navigate("/");
  };

  const handleMenuClick = (item) => {
    setOpen(false);
    setActiveSubmenu(null);

    // 🔹 SUBMENU
    if (item.type === "submenu") {
      setActiveSubmenu("menu");
      return;
    }

    if (item.type === "category") {
      setActiveSubmenu("category");
      return;
    }

    // 🔹 SCROLL
    if (item.type === "scroll") {
      scrollToSection(item.target);
      return;
    }

    // 🔹 EXTERNAL LINK
    if (item.target && item.target.startsWith("http")) {
      window.open(item.target, "_blank");
      return;
    }

    // 🔹 INTERNAL LINK
    if (item.path) {
      navigate(item.path);
    }
  };

  return (
    <nav className="fixed top-0 left-0 w-full h-[80px] bg-dry border-b border-gray-800 z-[999]">
      <div className="max-w-6xl mx-auto flex items-center justify-between p-4 ">
        {/* LOGO */}
        <img
          src="/saigonstar/logo.webp"
          width={150}
          className="cursor-pointer"
          onClick={goHome}
        />

        {/* DESKTOP */}
        <div className="hidden md:flex items-center gap-6 text-neonYellow">
          <button
            onClick={() => navigate("/search")}
            className="hover:text-darkbg transition"
          >
            <Search className="w-5 h-5" />
          </button>
          <div className="relative">
            <button onClick={handleUserClick} className="relative">
              <div className="relative">
                {user ? (
                  user.photo ? (
                    <img
                      src={user.photo}
                      alt={user.displayName || "User"}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-neonYellow text-navy flex items-center justify-center text-sm font-semibold">
                      {(user.displayName || user.username || "U")
                        .slice(0, 2)
                        .toUpperCase()}
                    </div>
                  )
                ) : (
                  <div className="w-8 h-8 rounded-full bg-navy flex items-center justify-center">
                    <User className="w-5 h-5 text-neonYellow" />
                  </div>
                )}

                {/* VERIFIED BADGE */}
                {user?.isVerified && (
                  <span className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-[2px] flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </span>
                )}
              </div>
            </button>
            <UserDropdown
              open={userMenuOpen}
              onClose={() => setUserMenuOpen(false)}
              onLogout={handleLogout}
            />
          </div>

          <button
            className="flex items-center gap-2 bg-neonYellow text-dry px-3 py-1.5"
            onClick={() => goTo("/cart")}
          >
            <ShoppingCart className="w-4 h-4" />
            <span>${cartTotal.toFixed(2)}</span>
          </button>

          <button onClick={() => setOpen(!open)}>
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {/* MOBILE ICONS */}
        <div className="flex gap-3 md:hidden">
          <button
            className="relative flex items-center justify-center w-10 h-10 rounded-md text-neonYellow"
            onClick={() => goTo("/cart")}
          >
            <ShoppingCart className="w-5 h-5" />

            <span
              className="absolute -bottom-0 left-0 flex items-center justify-center
                   w-5 h-5 text-[11px] font-semibold
                   bg-neonYellow text-darkbg
                   rounded-sm"
            >
              {cartCount}
            </span>
          </button>

          <button onClick={() => setOpen(!open)}>
            {open ? (
              <X className="text-neonYellow" size={26} />
            ) : (
              <Menu className="text-neonYellow" size={26} />
            )}
          </button>
        </div>
      </div>

      {/* ================= MOBILE DRAWER ================= */}
      <AnimatePresence>
        {open && (
          <>
            {/* OVERLAY */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black z-50]"
              onClick={() => {
                setOpen(false);
                setActiveSubmenu(null);
              }}
            />

            {/* DRAWER PANEL (OLD DARK STYLE KEPT) */}
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ duration: 0.35, ease: "easeInOut" }}
              className="fixed top-0 left-0 w-[100%] max-w-md h-screen bg-navy shadow-2xl overflow-hidden "
            >
              {/* TOP BAR */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-border min-h-[80px] z-[99]">
                <div className="flex gap-x-6">
                  <button
                    className="text-sm tracking-widest text-neonYellow uppercase"
                    onClick={() => {
                      navigate("/search");
                      setOpen(false);
                    }}
                  >
                    <Search size={16} />
                  </button>

                  <div className="relative">
                    <button
                      onClick={() => {
                        handleUserClick();
                        setOpen(false);
                      }}
                      className="relative"
                    >
                      <div className="relative">
                        {user ? (
                          user.photo ? (
                            <img
                              src={user.photo}
                              alt={user.displayName || "User"}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-neonYellow text-navy flex items-center justify-center text-sm font-semibold">
                              {(user.displayName || user.username || "U")
                                .slice(0, 2)
                                .toUpperCase()}
                            </div>
                          )
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-navy flex items-center justify-center">
                            <User className="w-5 h-5 text-neonYellow" />
                          </div>
                        )}

                        {/* VERIFIED BADGE */}
                        {user?.isVerified && (
                          <span className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-[2px] flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </span>
                        )}
                      </div>
                    </button>
                    {/* <UserDropdownMobileView
                      open={userMenuOpen}
                      onClose={() => setUserMenuOpen(false)}
                      onLogout={handleLogout}
                    /> */}
                  </div>
                </div>

                <button
                  onClick={() => setOpen(false)}
                  className="absolute top-4 right-4 z-[60]
    w-9 h-9 flex items-center justify-center
    rounded-full border border-neonYellow/40
    text-neonYellow hover:bg-neonYellow hover:text-darkbg
    transition duration-300 "
                >
                  <X size={20} />
                </button>
              </div>

              {/* SLIDING CONTENT */}
              <motion.div
                // animate={{ x: activeSubmenu ? "-50%" : "0%" }}
                animate={{
                  x:
                    activeSubmenu === "menu"
                      ? "-33.333%"
                      : activeSubmenu === "category"
                        ? "-66.666%"
                        : "0%",
                }}
                transition={{ duration: 0.35, ease: "easeInOut" }}
                className="flex w-[300%]"
              >
                {/* MAIN MENU */}
                <div className="w-1/3">
                  {t2.submenu.main.map((item, i) => {
                    const isAuthItem = item.auth;

                    // 👉 change label if not logged in
                    const displayLabel =
                      isAuthItem && !user
                        ? item.guestLabel || item.label
                        : item.label;

                    return (
                      <button
                        key={i}
                        onClick={() => {
                          // 👉 if guest → open login instead
                          if (isAuthItem && !user) {
                            dispatch(setAuthModalOpen(true));
                            setOpen(false);
                            return;
                          }

                          handleMenuClick(item);
                        }}
                        className="w-full flex items-center justify-between px-6 py-4 border-b border-border text-sm tracking-widest text-neonYellow uppercase"
                      >
                        {displayLabel}

                        {(item.type === "submenu" ||
                          item.type === "category") && (
                          <span className="text-lg">›</span>
                        )}
                      </button>
                    );
                  })}
                  <WriteSection />
                </div>

                {/* SUB MENU */}
                <div className="w-1/3">
                  <button
                    onClick={() => setActiveSubmenu(null)}
                    className="flex w-full items-center gap-3 px-6 py-5 border-b border-border text-sm tracking-widest text-neonYellow uppercase font-semibold"
                  >
                    <span className="text-lg text-neonYellow animate-neonFlicker">
                      ‹
                    </span>
                    {t2.submenu.back}
                  </button>

                  {t2.submenu.menuChildren.map((item, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        navigate(item.path);

                        // 👇 Detect Our Story
                        // if (item.type === "scroll") {
                        //   scrollToSection(item.target);
                        //   return;
                        // }

                        if (item.label.toLowerCase().includes("online")) {
                          scrollToSection("#category");
                          return;
                        }
                        setOpen(false);
                        setActiveSubmenu(null);
                      }}
                      className="w-full px-6 py-6 border-b border-border text-sm tracking-widest text-neonYellow uppercase text-left"
                    >
                      {item.label}
                    </button>
                  ))}
                  <WriteSection />
                </div>

                {/* CATEGORY SUBMENU */}
                <div className="w-1/3  ">
                  <div className="scrollbar-thin overflow-y-auto max-h-[50vh]">
                    <button
                      onClick={() => setActiveSubmenu(null)}
                      className="flex w-full items-center gap-3 px-6 py-5 border-b border-border text-sm tracking-widest text-neonYellow capitalized font-semibold"
                    >
                      <span className="text-lg text-neonYellow">‹</span>
                      {t2.submenu.back}
                    </button>

                    {menuCategory.map((cat) => (
                      <button
                        key={cat._id}
                        onClick={() => {
                          dispatch(addCategoryToCart(cat));

                          navigate(`/menu`);
                          setOpen(false);
                          setActiveSubmenu(null);
                        }}
                        className="w-full px-6 py-3 border-b border-border text-sm tracking-widest text-neonYellow uppercase text-left hover:bg-white/5 transition"
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                  <WriteSection />
                </div>
              </motion.div>
              <button
                onClick={() => setOpen(false)}
                className="absolute top-4 -right-12 z-[60]
             w-10 h-10 flex items-center justify-center
             rounded-full border border-neonYellow/40
             text-neonYellow hover:bg-neonYellow hover:text-darkbg
             transition duration-300"
              >
                <X size={22} />
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
